import React from 'react';
import { Handle, Position } from 'reactflow';
import { Terminal } from 'lucide-react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

interface ATCommandNodeData {
  label?: string;
  atCommand?: {
    command?: string;
    expectedResponse?: string;
    acceptAnyResponse?: boolean;
    timeout?: number;
    retries?: number;
    parseResponse?: boolean;
    storeAs?: string;
  };
  executionMode?: 'sync' | 'async';
}

export default function ATCommandNode({ 
  data, 
  selected 
}: { 
  data: ATCommandNodeData; 
  selected?: boolean 
}) {
  return (
    <div className={cn(
      "min-w-[170px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected 
        ? "border-accent-blue shadow-glow-focus" 
        : "border-accent-blue/50 dark:border-accent-blue/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-accent-blue !border-accent-blue"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-accent-blue/10 dark:bg-accent-blue/20 flex items-center justify-center">
          <Terminal className="w-3.5 h-3.5 text-accent-blue" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            AT Command
          </div>
          <div className="text-xs text-muted-foreground/70 font-mono truncate">
            {data.atCommand?.command || 'Not configured'}
          </div>
        </div>
        {data.executionMode && (
          <Badge
            variant={data.executionMode === 'async' ? 'outline' : 'secondary'}
            className={data.executionMode === 'async' ? 'border-accent-yellow text-accent-yellow' : 'bg-accent-green text-white'}
          >
            {data.executionMode}
          </Badge>
        )}
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {data.atCommand?.acceptAnyResponse ? (
          <div className="text-xs">
            <span className="text-accent-green font-medium">
              ✓ Any Response
            </span>
          </div>
        ) : data.atCommand?.expectedResponse && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium">
              Expect:
            </span>
            <span className="ml-1 text-foreground font-mono">
              {data.atCommand.expectedResponse}
            </span>
          </div>
        )}

        {data.atCommand?.storeAs && (
          <div className="text-xs">
            <span className="text-accent-blue font-medium font-mono">
              → ${data.atCommand.storeAs}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-border dark:border-border-subtle">
          <span className="text-xs text-muted-foreground/70">
            {data.atCommand?.timeout ? `${data.atCommand.timeout}ms` : '5000ms'}
          </span>
          {data.atCommand?.retries !== undefined && data.atCommand.retries > 0 && (
            <span className="text-xs text-muted-foreground/70">
              ↻ {data.atCommand.retries}
            </span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-accent-blue !border-accent-blue"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}

